import styled from '@emotion/styled'
import { Button, spacing, Text } from '@retailer-platform/shared-components'
import React, { useCallback, useState } from 'react'
import { useDomainMessages } from '../../../../utils/domain/intl'
import PublishDataGridReadOnlyRow from './PublishDataGridReadOnlyRow'
import PublishDataGridEditRow from './PublishDataGridEditRow'

export interface Datum {
  attributeName: string
  campField: string
  required: boolean
}

interface PublishDataGridProps {
  data: Datum[]
  onUpdate: (data: Datum[]) => void
  disabled: boolean
}

const Center = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.X16,
  justifyContent: 'center',
  alignItems: 'center',
})

const Grid = styled.div({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr auto auto',
  gap: spacing.X16,
  width: '100%',
})

const HeaderRow = () => (
  <>
    <Text weight="semibold" size="small">
      Attribute Name
    </Text>
    <Text weight="semibold" size="small">
      Camp Field
    </Text>
    <Text weight="semibold" size="small">
      Required
    </Text>
    <Text weight="semibold" size="small">
      Actions
    </Text>
  </>
)

const PublishDataGrid: React.FC<PublishDataGridProps> = ({ data, onUpdate, disabled }) => {
  const [editingIndexes, setEditingIndexes] = useState<Set<number>>(new Set())
  const [newIndexes, setNewIndexes] = useState<Set<number>>(new Set())
  const i18n = useDomainMessages({
    addDatum: 'catalogAdminDomain.attributeManagement.stepAttributeForms.publishForm.addDatum',
  })
  const onAdd = useCallback(() => {
    onUpdate([...data, { attributeName: '', campField: '', required: false }])
    setNewIndexes(new Set([...newIndexes, data.length]))
    setEditingIndexes(new Set([...editingIndexes, data.length]))
  }, [data, onUpdate, editingIndexes, newIndexes])

  const onDelete = useCallback(
    (index: number) => {
      onUpdate(data.filter((_, i) => i !== index))
      setEditingIndexes(new Set([...editingIndexes].filter(i => i !== index)))
      setNewIndexes(new Set([...newIndexes].filter(i => i !== index)))
    },
    [data, onUpdate, editingIndexes, newIndexes]
  )

  const onUpdateDatum = useCallback(
    (index: number, datum: Datum) => {
      onUpdate(data.map((d, i) => (i === index ? datum : d)))
      setEditingIndexes(new Set([...editingIndexes].filter(i => i !== index)))
      setNewIndexes(new Set([...newIndexes].filter(i => i !== index)))
    },
    [data, onUpdate, editingIndexes, newIndexes]
  )

  const onEdit = useCallback(
    (index: number) => {
      setEditingIndexes(new Set(editingIndexes.add(index)))
    },
    [editingIndexes]
  )

  const onCancel = useCallback(
    (index: number) => {
      if (newIndexes.has(index)) {
        onUpdate(data.filter((_, i) => i !== index))
        setNewIndexes(new Set([...newIndexes].filter(i => i !== index)))
      } else {
        setEditingIndexes(new Set([...editingIndexes].filter(i => i !== index)))
      }
    },
    [editingIndexes, newIndexes, data, onUpdate]
  )

  return (
    <Center>
      <Grid>
        <HeaderRow />
        {data.map((datum, index) =>
          editingIndexes.has(index) ? (
            <PublishDataGridEditRow
              key={index}
              datum={datum}
              onUpdate={(d: Datum) => onUpdateDatum(index, d)}
              onCancel={() => onCancel(index)}
              attributeNameBlacklist={data.filter((_, i) => i !== index).map(d => d.attributeName)}
            />
          ) : (
            <PublishDataGridReadOnlyRow
              key={index}
              datum={datum}
              onEdit={() => onEdit(index)}
              onDelete={() => onDelete(index)}
              disabled={disabled}
            />
          )
        )}
      </Grid>
      <Button variant="primary" onClick={onAdd} disabled={disabled}>
        {i18n.addDatum}
      </Button>
    </Center>
  )
}

export default PublishDataGrid
